import type { OrderWithItems } from "@/types/pos";
import { ChevronLeft, MapPin, Clock, Flame, Leaf, Flag, ChevronRight, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import placeholder from "@/assets/pos/placeholder.png";
import { STATUS_MAP, STATUS_STEPS } from "@/lib/pos/global";
import { useState } from "react";
import OrderDetail from "@/components/pos/order-detail";
import { usePosOrders } from "@/hooks/pos/use-order";
import noItemAvailable from "@/assets/pos/shopping-cart.svg";
import CardBrandIcon from "@/components/CardBrandIcon";

interface OrderHistoryProps {
  onBackToLocation: () => void;
}

export default function OrderHistory({ onBackToLocation }: OrderHistoryProps) {
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  const { orders, isLoading } = usePosOrders();

  const isCardOrder = (order: OrderWithItems) =>
    order.paymentMethod === 'CARD';

  // Get the selected order from the orders array so it updates when orders refresh
  const selectedOrder = selectedOrderId !== null
    ? orders.find(o => o.id === selectedOrderId) ?? null
    : null;

  const getCurrentStepIndex = (status?: string) => {
    const s = status?.toLowerCase() || "pending";

    let idx = STATUS_STEPS.findIndex((st) => st.key === s);
    if (idx === -1) idx = 0;

    return idx;
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const aStatus = a.status?.toLowerCase();
    const bStatus = b.status?.toLowerCase();

    const aDelivered = aStatus === "delivered";
    const bDelivered = bStatus === "delivered";

    if (aDelivered !== bDelivered) {
      return aDelivered ? 1 : -1;
    }
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();

    return bTime - aTime;
  });

  return (
    <div className="w-full max-w-5xl mx-auto px-5 pb-10 bg-[var(--pos-secondary-background-color)] min-h-screen pt-[63px] relative">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-[var(--pos-secondary-background-color)]">
      <div className="flex items-center gap-3 py-4 max-w-5xl mx-auto px-5">
        <button
          type="button"
          aria-label="Go back"
          onClick={onBackToLocation}
          className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
        </button>
        <h1 className="text-[20px] leading-[18px] font-bold text-[var(--pos-primary-text-color)] font-montserrat">Order History</h1>
      </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse bg-[var(--pos-accordian-disabled-btn-background)] h-24 rounded-xl"
            />
          ))}
        </div>
      ) : !sortedOrders?.length ? (
        <div className="flex items-center justify-center flex-col gap-2 h-full text-white">
						<img src={noItemAvailable} alt="No orders yet" />
						<span className="font-montserrat font-semibold text-[14px] leading-[18px] tracking-[0] text-center align-middle">No Orders Yet</span>
						<p className="font-figtree font-medium text-[12px] leading-[16px] tracking-[0] text-center align-middle text-[var(--pos-secondary-text-color)] px-2">You haven't placed any orders yet. Once you do, they'll show up here for easy tracking.</p>
        </div>
      ) : (
        <div className="space-y-3 pb-[62px]">
          {sortedOrders.map((order) => {
            const currentIndex = getCurrentStepIndex(order.status);

            return (
              <div
                key={order.id}
                className="bg-[var(--pos-primary-background-color)] rounded-sm py-3 px-4"
              >
                {/* STATUS BAR (icons + active/fill/not-fill) */}
                {!(order.status?.toLowerCase() === "delivered") && (
                  <div className="border-b border-dashed border-[var(--pos-layout-border-color)] w-full mb-4 pb-4">
                    <div className="flex justify-between relative">
                      {STATUS_STEPS.map((step, index) => {
                        const Icon = step.Icon;
                        const state =
                          index < currentIndex
                            ? "filled"
                            : index === currentIndex
                            ? "active"
                            : "notFilled";

                        const style = {
                          filled:
                            "text-green-400 bg-green-900/30",
                          active:
                            "text-yellow-400 bg-yellow-900/30",
                          notFilled: "text-[var(--pos-disabled-text-color)] bg-[var(--pos-accordian-disabled-btn-background)]",
                        }[state];

                        return (
                          <div
                            key={step.key}
                            className="flex flex-col items-center w-[27%] relative first:ml-[calc(-12.5%+24px)] last:mr-[calc(-12.5%+26px)]"
                          >
                            {/* connecting line */}
                            {index !== STATUS_STEPS?.length - 1 && (
                              <div
                                className={`absolute top-[15px] left-1/2 h-[1px] w-full -translate-y-1/2 ${
                                  index < currentIndex
                                    ? "bg-green-500"
                                    : "bg-[var(--pos-accordian-disabled-btn-background)]"
                                }`}
                              />
                            )}

                            {/* circle + icon */}
                            <div
                              className={`rounded-full h-[26px] w-[26px] flex items-center justify-center z-10 ${style}`}
                            >
                              <Icon className="h-4 w-4" />
                            </div>

                            {/* label */}
                            <p
                              className={`text-[11px] leading-[14px] font-medium font-montserrat mt-1 ${
                                state === "filled" || state === "active"
                                  ? "text-[var(--pos-primary-text-color)]"
                                  : "text-[var(--pos-secondary-text-color)]"
                              }`}
                            >
                              {step.label}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Order header row */}
                <div className="flex justify-between items-center">
                  <div className="flex flex-col">
                    <p className="font-bold text-sm leading-4 text-[var(--pos-primary-text-color)] font-figtree pb-1">
                      Order ID: #{order?.orderNumber}
                    </p>
                    <div className="flex items-center text-xs gap-1">
                      <MapPin className="h-4 w-4 text-[var(--pos-placeholder-color)]" />
                      <span className="text-xs leading-5 font-regular text-[var(--pos-secondary-text-color)]">
                        {order?.location?.name}{" "}
                        {order?.locationDetails &&
                          `. ${order?.locationDetails}`}
                      </span>
                    </div>
                  </div>

                  {(() => {
                    const statusKey = order?.status?.toLowerCase() || "pending";
                    const status = STATUS_MAP[statusKey];

                    return (
                      <span
                        className={`text-xs leading-[14px] font-semibold font-figtree flex items-center gap-1 ${
                          status?.textColor || "text-yellow-600"
                        }`}
                      >
                        {status && <status.Icon className="h-4 w-4" />}
                        {status?.label || order?.status}
                      </span>
                    );
                  })()}
                </div>

                {/* ITEMS LIST */}
                <div className="border-y border-dashed border-[var(--pos-layout-border-color)] pt-3 mt-3">
                  {order.items?.length ? (
                    order.items.map((item, index) => {
                      // IMAGE LOGIC
                      const imageSrc = item.product?.imageUrl
                        ? item.product.imageUrl
                        :  item.customDrink?.components?.find((c) => c.type === "spirit" && c.imageUrl)?.imageUrl || placeholder;

                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 pb-3"
                        >
                          {/* IMAGE */}
                          <img
                            src={imageSrc}
                            alt={
                              item.product?.name ||
                              item.customDrink?.name ||
                              "Drink image"
                            }
                            className="h-[42px] w-[42px] rounded-lg object-cover flex-shrink-0"
                          />

                          {/* TEXT */}
                          <div className="flex flex-col gap-1">
                            {/* NAME + FLAGS inline */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-figtree leading-4 font-medium text-[var(--pos-primary-text-color)] mb-0">
                                {item.quantity} x{" "}
                                {item.product
                                   ? (
                                    <>
                                      {item?.product.name}
                                      {item?.product?.isRaw && <span className="text-[#E24A4A] ml-1">*</span>}
                                    </>
                                  ) : (
                                    item.customDrink?.name
                                )}
                              </p>
                              {item?.product && (
                                <>
                                  {item?.product?.isSpicy && (
                                    <Flame className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
                                  )}
                                  {item?.product?.isVegan && (
                                    <Leaf className="w-4 h-4 text-[#22C55E] fill-[#22C55E]" />
                                  )}
                                  {item?.product?.isSignature && (
                                    <Flag className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
                                  )}
                                </>
                              )}
                            </div>

                            {/* MODIFIERS */}
                            {item.modifiers && item.modifiers.length > 0 && (
                              <p className="text-xs font-figtree leading-[14px] font-medium text-[var(--pos-secondary-text-color)]">
                                {item.modifiers.map((mod, idx) => (
                                  <span key={mod.id}>
                                    {mod.modifierName}
                                    {idx < item.modifiers!.length - 1 ? ", " : ""}
                                  </span>
                                ))}
                              </p>
                            )}

                            {/* SUB-LABEL */}
                            {!item?.product && (
                              <p className="text-[11px] text-green-600">
                                Custom Drink
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-xs text-[var(--pos-secondary-text-color)]">No items</p>
                  )}
                </div>

                {/* Special Notes */}
                {order.notes && (
                  <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-700/40 rounded">
                    <span className="text-xs font-medium text-yellow-400">Note: </span>
                    <span className="text-xs text-yellow-400">{order.notes}</span>
                  </div>
                )}

                <div
                  className="flex items-center justify-between mt-3 cursor-pointer"
                  onClick={() => {
                    setSelectedOrderId(order.id);
                  }}
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1 text-xs leading-[14px] font-medium font-figtree text-[var(--pos-secondary-text-color)]">
                      {/* <Clock className="h-3 w-3" /> */}
                      <span>
                        Order placed on{" "}
                        {new Date(order.createdAt).toLocaleDateString(
                          undefined,
                          {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                          }
                        )}{" "}
                        @{" "}
                        {new Date(order.createdAt).toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    {/* PRICE + PAYMENT METHOD */}
                    <div className="flex justify-start gap-2 items-center">
                      <p className="px-3 h-[34px] rounded-lg bg-[var(--pos-negative-selected-color)] text-[--pos-primary-color] text-sm leading-4 font-bold font-figtree flex items-center justify-center">
                        {isCardOrder(order)
                          ? `$${order.totalUsd}`
                          : `${parseFloat(order.totalSech ?? "0").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} SECH`
                        }
                      </p>
                      {isCardOrder(order) ? (
                        <div className="flex items-center gap-1">
                          <CardBrandIcon brand={order.cardBrand} width={28} />
                          {order.cardLast4 && (
                            <span className="text-[var(--pos-secondary-text-color)] font-figtree font-medium text-xs leading-[18px]">
                              ••••{order.cardLast4}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Wallet className="h-4 w-4 text-[#258750]" />
                          {order.walletAddress && (
                            <span className="text-[#258750] font-figtree font-medium text-xs leading-[18px]">
                              {order.walletAddress.slice(0, 6)}...{order.walletAddress.slice(-4)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button className="h-5 w-5 p-0 flex items-center justify-center rounded-full bg-transparent">
                    <ChevronRight className="h-5 w-5 text-[var(--pos-placeholder-color)]"/>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selectedOrder && (
        <div className="fixed top-0 right-0 w-full h-full bg-[var(--pos-secondary-background-color)] z-[51] animate-slideIn">
          <OrderDetail
            order={selectedOrder}
            onClose={() => {
              setSelectedOrderId(null);
            }}
          />
        </div>
      )}
    </div>
  );
}
